import { useState } from 'react';
import { FormFieldInputType } from 'models/enum';
import { ChartMetaFormField, ContentItem } from 'models/ui';
import { ChartPermission, ErrorCard, ErrorFormField } from 'components/common';
import { ELSButton, ELSFlex, ELSFlexItem, ELSIcon } from 'components/common/els';
import { ChartViewProps } from 'components/features/shared/constants';
import {
  AnnualAmbulatoryFieldConfig,
  AnnualAmbulatoryStepConfig,
  COLLECTION_CONFIGS,
  CONDITIONAL_FIELDS,
  CollectionConfig,
  CollectionId,
  CollectionRow,
  FIELD_CONFIG_MAP,
  FormField,
  STEP_CONFIGS,
  VACCINE_CONFIGS,
  ViewTitle,
  characterCountText,
  controllerFieldSatisfies,
  formatOtherCellValue,
  hasFieldValue
} from './constants';
import AnnualAmbulatoryCareAddModal, { ModalRow } from './AnnualAmbulatoryCareAddModal';
import AnnualAmbulatoryCareInlineCollection from './AnnualAmbulatoryCareInlineCollection';
import AnnualAmbulatoryCareSelect from './AnnualAmbulatoryCareSelect';
import AnnualAmbulatoryCareTimeField from './AnnualAmbulatoryCareTimeField';
import './annual.ambulatory.care.scss';

export { characterCountText };

export interface AnnualAmbulatoryCareViewProps extends ChartViewProps {
  activeStep: number;
  collections: Record<CollectionId, CollectionRow[]>;
  completedSteps?: boolean[];
  displayAuthoringData?: Function;
  enableDisplayRecordsButton?: boolean;
  furthestStep: number;
  isAuthor: boolean;
  onBackStep: VoidFunction;
  onContinueStep: VoidFunction;
  onReplaceCollection: (collectionId: CollectionId, rows: CollectionRow[]) => void;
  onSelectStep: (stepIndex: number) => void;
}

const getFieldConfig = (fieldName: FormField): AnnualAmbulatoryFieldConfig | undefined => FIELD_CONFIG_MAP.get(fieldName);

const getRenderedField = (formFieldMap: Map<string, ChartMetaFormField>, fieldName: FormField, isAuthor: boolean): ChartMetaFormField | undefined => {
  const formField = formFieldMap.get(fieldName);
  if (!formField) return formField;
  return isAuthor ? formField : { ...formField, disabled: true };
};

const withClearDropdownOption = (formField: ChartMetaFormField): ChartMetaFormField => {
  const content = formField.chartContent;
  if (!content?.length) return formField;
  const emptyOption = content.find((item) => String(item.value ?? '') === '');
  if (!emptyOption) return formField;
  const realOptions = content.filter((item) => String(item.value ?? '') !== '');
  const chartContent = [
    { ...emptyOption, name: '', label: '', hidden: true, disabled: false },
    { ...emptyOption, name: 'Clear', label: 'Clear', hidden: false, disabled: false },
    ...realOptions
  ];
  return { ...formField, chartContent };
};

export const CLEAR_MULTISELECT_VALUE = '__clear_all__';

export const withClearMultiSelectOption = (formField: ChartMetaFormField): ChartMetaFormField => {
  const content = formField.chartContent;
  if (!content?.length) return formField;
  const delegateOnChange = content[0].onChange;

  const handleOptionChange = (field: ChartMetaFormField, clickedOption: ContentItem) => {
    if (String(clickedOption?.value) === CLEAR_MULTISELECT_VALUE) {
      const clearedContent = content.map((item) => ({ ...item, selected: false }));
      if (formField.onChange) formField.onChange({ ...formField, chartContent: clearedContent, value: '' }, { target: { value: '' } });
      return;
    }
    if (delegateOnChange) delegateOnChange(field, clickedOption);
  };

  const clearOption: ContentItem = {
    ...content[0],
    id: CLEAR_MULTISELECT_VALUE,
    name: 'Clear',
    label: 'Clear',
    value: CLEAR_MULTISELECT_VALUE,
    selected: false,
    isHidden: false,
    onChange: handleOptionChange
  };

  return { ...formField, chartContent: [clearOption, ...content] };
};

export const isConditionalFieldHidden = (formFieldMap: Map<string, ChartMetaFormField>, fieldName: FormField): boolean => {
  const conditional = CONDITIONAL_FIELDS[fieldName];
  if (!conditional) return false;
  return !controllerFieldSatisfies(formFieldMap.get(conditional.controller), conditional.whenValue);
};

export const usesCustomDropdown = (fieldConfig: AnnualAmbulatoryFieldConfig): boolean =>
  fieldConfig.type === FormFieldInputType.DROPDOWN && (fieldConfig.name.startsWith('imm') || Boolean(fieldConfig.customSelect));

const getFieldWidth = (row: FormField[]): string => {
  if (row.length >= 3) return '1o3';
  if (row.length === 2) return '1o2';
  return '1o1';
};

export const getRowWidth = (row: FormField[], widthOverride?: string): string => widthOverride ?? getFieldWidth(row);

const TEXT_INPUT_TYPES = [FormFieldInputType.TEXT_BOX, FormFieldInputType.TEXT_AREA];

export const shouldShowCharCount = (fieldConfig: AnnualAmbulatoryFieldConfig): boolean =>
  Boolean(fieldConfig.showCharCount) && TEXT_INPUT_TYPES.includes(fieldConfig.type) && typeof fieldConfig.maxLength === 'number';

export const getFieldInputProps = (fieldConfig: AnnualAmbulatoryFieldConfig): Record<string, unknown> | undefined => {
  if (shouldShowCharCount(fieldConfig)) return { maxLength: fieldConfig.maxLength };
  if (fieldConfig.type === FormFieldInputType.MULTISELECT_DROPDOWN) return { placeholder: 'Select', showSearchIcon: false, disableSearch: true };
  return undefined;
};

const renderCharacterCount = (formField: ChartMetaFormField, maxLength: number) => (
  <div className="annual-ambulatory-care__char-count">{characterCountText(formField.value, maxLength)}</div>
);

const renderFieldInner = (formFieldMap: Map<string, ChartMetaFormField>, formSubmittedCount: number, formField: ChartMetaFormField, fieldConfig: AnnualAmbulatoryFieldConfig) => {
  if (fieldConfig.type === FormFieldInputType.TIME) {
    return (
      <div className="annual-ambulatory-care__field">
        <div className="chart-input__label">{formField.label}</div>
        <AnnualAmbulatoryCareTimeField formField={formField} />
      </div>
    );
  }

  if (fieldConfig.type === FormFieldInputType.RADIO_CHOICE) {
    const labelClassName = ['annual-ambulatory-care__inline-radio-label', fieldConfig.noWrapLabel && 'annual-ambulatory-care__inline-radio-label--nowrap']
      .filter(Boolean)
      .join(' ');
    return (
      <div className="annual-ambulatory-care__inline-radio">
        <span className={labelClassName}>{formField.label}</span>
        <ErrorFormField
          className="annual-ambulatory-care__inline-radio-field"
          formField={formField}
          hideAsterisk
          formSubmittedCount={formSubmittedCount}
          formFieldMap={formFieldMap}
          options={{ displayHorizontal: true }}
        />
      </div>
    );
  }

  const isDropdown = fieldConfig.type === FormFieldInputType.DROPDOWN;

  if (usesCustomDropdown(fieldConfig)) {
    return (
      <div className="annual-ambulatory-care__field">
        <div className="chart-input__label">{formField.label}</div>
        <AnnualAmbulatoryCareSelect formField={formField} />
      </div>
    );
  }

  const isMultiSelect = fieldConfig.type === FormFieldInputType.MULTISELECT_DROPDOWN;
  let renderedFormField = formField;
  if (isDropdown) renderedFormField = withClearDropdownOption(formField);
  else if (isMultiSelect) renderedFormField = withClearMultiSelectOption(formField);
  const showCharCount = shouldShowCharCount(fieldConfig);
  const wrapperClassName =
    [isDropdown && !hasFieldValue(formField) && 'annual-ambulatory-care__dropdown-placeholder', showCharCount && 'annual-ambulatory-care__char-field'].filter(Boolean).join(' ') ||
    undefined;

  return (
    <div className={wrapperClassName}>
      <ErrorFormField
        className="annual-ambulatory-care__field"
        formField={renderedFormField}
        // Nothing on this chart is mandatory (SIMSNG-6062). The asterisk is suppressed here rather
        // than left to config so a REQUIRED rule still served by chart metadata cannot resurrect it.
        hideAsterisk
        formSubmittedCount={formSubmittedCount}
        formFieldMap={formFieldMap}
        inputProps={getFieldInputProps(fieldConfig)}
        options={fieldConfig.displayHorizontal ? { displayHorizontal: true } : undefined}
        useDefaultMargin={!showCharCount}
      />
      {showCharCount && renderCharacterCount(formField, fieldConfig.maxLength as number)}
    </div>
  );
};

const renderField = (formFieldMap: Map<string, ChartMetaFormField>, formSubmittedCount: number, isAuthor: boolean, fieldName: FormField, width: string, fixedWidth: boolean) => {
  if (isConditionalFieldHidden(formFieldMap, fieldName)) return null;

  const formField = getRenderedField(formFieldMap, fieldName, isAuthor);
  const fieldConfig = getFieldConfig(fieldName);
  if (!formField || !fieldConfig) return null;

  const inner = renderFieldInner(formFieldMap, formSubmittedCount, formField, fieldConfig);

  if (fixedWidth) {
    return (
      <div className="annual-ambulatory-care__fixed-item" key={fieldName}>
        {inner}
      </div>
    );
  }

  return (
    <ELSFlexItem key={fieldName} md="1o1" w={width}>
      {inner}
    </ELSFlexItem>
  );
};

const renderFieldRow = (
  formFieldMap: Map<string, ChartMetaFormField>,
  formSubmittedCount: number,
  isAuthor: boolean,
  row: FormField[],
  fixedWidth = false,
  widthOverride?: string
) => {
  const width = getRowWidth(row, widthOverride);
  const fields = row.map((fieldName) => renderField(formFieldMap, formSubmittedCount, isAuthor, fieldName, width, fixedWidth));
  if (fixedWidth) {
    return (
      <div className="annual-ambulatory-care__fixed-row" key={row.join('-')}>
        {fields}
      </div>
    );
  }
  return (
    <ELSFlex className="u-flex-gutter-3x" key={row.join('-')} left wrap>
      {fields}
    </ELSFlex>
  );
};

const formatCollectionCell = (row: CollectionRow, column: CollectionConfig['columns'][number]): string => {
  const base = String(row[column.field] ?? '').trim();
  const other = column.otherField ? String(row[column.otherField] ?? '').trim() : '';
  return formatOtherCellValue(base, other);
};

const renderCollectionTable = (config: CollectionConfig, rows: CollectionRow[], isAuthor: boolean, onEdit: (collectionId: CollectionId) => void) => (
  <div className="annual-ambulatory-care__collection" key={config.id}>
    <div className="annual-ambulatory-care__collection-header">
      <span className="annual-ambulatory-care__collection-title">{config.title}</span>
      {isAuthor && rows.length > 0 && (
        <button className="annual-ambulatory-care__collection-edit" type="button" onClick={() => onEdit(config.id)} aria-label={`Edit ${config.title}`}>
          <ELSIcon name="edit" size="1x" />
        </button>
      )}
    </div>

    {rows.length > 0 && (
      <table className={`annual-ambulatory-care__table annual-ambulatory-care__table--${config.id}`}>
        <thead>
          <tr>
            {config.columns.map((column) => (
              <th key={column.field}>{column.header}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, rowIndex) => (
            // eslint-disable-next-line react/no-array-index-key
            <tr key={rowIndex}>
              {config.columns.map((column) => (
                <td key={column.field}>{formatCollectionCell(row, column)}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    )}

    {isAuthor && rows.length === 0 && (
      <ELSButton
        className="annual-ambulatory-care__add-btn"
        text="Add"
        type="secondary"
        iconName="add-circle"
        iconPrefix="hmds"
        iconPosition="right"
        iconSize="1x"
        onClick={() => onEdit(config.id)}
      />
    )}
  </div>
);

const renderImmunizations = (formFieldMap: Map<string, ChartMetaFormField>, formSubmittedCount: number, isAuthor: boolean) => (
  <>
    {VACCINE_CONFIGS.map((vaccine) => (
      <div className="annual-ambulatory-care__immunization" key={vaccine.label}>
        <h5 className="annual-ambulatory-care__immunization-label">{vaccine.label}</h5>
        {renderFieldRow(formFieldMap, formSubmittedCount, isAuthor, [vaccine.statusField, vaccine.dateField, vaccine.boosterField])}
      </div>
    ))}
    <div className="annual-ambulatory-care__immunization" key="other">
      <h5 className="annual-ambulatory-care__immunization-label">Other:</h5>
      {renderFieldRow(formFieldMap, formSubmittedCount, isAuthor, [FormField.IMM_OTHER_IMMUNIZATION, FormField.IMM_OTHER_DATE, FormField.IMM_OTHER_BOOSTER])}
      {renderFieldRow(formFieldMap, formSubmittedCount, isAuthor, [FormField.IMM_OTHER_STATUS], false, '1o3')}
    </div>
  </>
);

interface StepPanelProps {
  stepConfig: AnnualAmbulatoryStepConfig;
  formFieldMap: Map<string, ChartMetaFormField>;
  formSubmittedCount: number;
  isAuthor: boolean;
  collections: Record<CollectionId, CollectionRow[]>;
  onEditCollection: (collectionId: CollectionId) => void;
  onReplaceCollection: (collectionId: CollectionId, rows: CollectionRow[]) => void;
}

const renderCollection = (
  collectionId: CollectionId,
  collections: Record<CollectionId, CollectionRow[]>,
  isAuthor: boolean,
  onEditCollection: (collectionId: CollectionId) => void,
  onReplaceCollection: (collectionId: CollectionId, rows: CollectionRow[]) => void
) => {
  const config = COLLECTION_CONFIGS[collectionId];
  if (config.inlineEntry) {
    return (
      <AnnualAmbulatoryCareInlineCollection
        key={collectionId}
        config={config}
        rows={collections[collectionId]}
        isAuthor={isAuthor}
        onReplace={(rows) => onReplaceCollection(collectionId, rows)}
      />
    );
  }
  return renderCollectionTable(config, collections[collectionId], isAuthor, onEditCollection);
};

const sectionHasHeading = (section: AnnualAmbulatoryStepConfig['sections'][number], sectionIndex: number, stepConfig: AnnualAmbulatoryStepConfig): boolean =>
  Boolean(section.title) && !(sectionIndex === 0 && section.title === stepConfig.title);

export const getSectionClassName = (section: AnnualAmbulatoryStepConfig['sections'][number], sectionIndex: number, stepConfig: AnnualAmbulatoryStepConfig): string => {
  const divided = sectionIndex > 0;
  const flush = divided && !sectionHasHeading(section, sectionIndex, stepConfig) && section.rows.length > 0;
  return [
    'annual-ambulatory-care__section',
    divided && 'annual-ambulatory-care__section--divided',
    divided && section.lightDivider && 'annual-ambulatory-care__section--divided-light',
    flush && 'annual-ambulatory-care__section--flush'
  ]
    .filter(Boolean)
    .join(' ');
};

const StepPanel = ({ stepConfig, formFieldMap, formSubmittedCount, isAuthor, collections, onEditCollection, onReplaceCollection }: StepPanelProps) => {
  if (stepConfig.title === 'Immunizations') return renderImmunizations(formFieldMap, formSubmittedCount, isAuthor);

  return (
    <>
      {stepConfig.sections.map((section, sectionIndex) => {
        const showHeading = sectionHasHeading(section, sectionIndex, stepConfig);
        const sectionClassName = getSectionClassName(section, sectionIndex, stepConfig);
        return (
          <div className={sectionClassName} key={section.title || `section-${sectionIndex}`}>
            {showHeading && <h4 className="annual-ambulatory-care__section-title">{section.title}</h4>}
            {section.rows.map((row) => renderFieldRow(formFieldMap, formSubmittedCount, isAuthor, row, section.fixedWidth, section.width))}
            {section.collections?.map((collectionId) => renderCollection(collectionId, collections, isAuthor, onEditCollection, onReplaceCollection))}
          </div>
        );
      })}
    </>
  );
};

const getPrimaryButtonText = (isLastStep: boolean, isAuthor: boolean): string => {
  if (!isLastStep) return isAuthor ? 'Save & Next' : 'Continue';
  return isAuthor ? 'Save Records' : 'Complete';
};

export const showsPrimaryAction = (isLastStep: boolean, isAuthor: boolean): boolean => isAuthor || !isLastStep;

interface StepActionsProps {
  activeStep: number;
  displayAuthoringData?: Function;
  enableDisplayRecordsButton?: boolean;
  isAuthor: boolean;
  isLastStep: boolean;
  onBackStep: VoidFunction;
  onContinueStep: VoidFunction;
}

const StepActions = ({ activeStep, displayAuthoringData, enableDisplayRecordsButton, isAuthor, isLastStep, onBackStep, onContinueStep }: StepActionsProps) => {
  const isSaveRecords = isLastStep && isAuthor;
  return (
    <ChartPermission>
      {({ allowSaveFragment }) => (
        <div className="annual-ambulatory-care__actions">
          {activeStep > 0 && <ELSButton onClick={onBackStep} text="Back" type="secondary" iconName="chevron-left" iconPosition="left" iconSize="1x" />}
          {showsPrimaryAction(isLastStep, isAuthor) && (
            <ELSButton
              className={isSaveRecords ? 'annual-ambulatory-care__save-btn' : undefined}
              isDisabled={isLastStep && !allowSaveFragment}
              onClick={onContinueStep}
              text={getPrimaryButtonText(isLastStep, isAuthor)}
              type="primary"
              iconName={isSaveRecords ? undefined : 'chevron-right'}
              iconPosition="right"
              iconSize="1x"
            />
          )}
          {isSaveRecords && <ELSButton type="secondary" text="Return with data" isDisabled={!enableDisplayRecordsButton} onClick={displayAuthoringData} />}
        </div>
      )}
    </ChartPermission>
  );
};
StepActions.displayName = 'StepActions';

const AnnualAmbulatoryCareView = ({
  activeStep,
  collections,
  completedSteps = [],
  displayAuthoringData,
  enableDisplayRecordsButton,
  formFieldMap,
  formSubmittedCount,
  furthestStep,
  isAuthor,
  onBackStep,
  onContinueStep,
  onReplaceCollection,
  onSelectStep
}: AnnualAmbulatoryCareViewProps) => {
  const [modalCollectionId, setModalCollectionId] = useState<CollectionId | null>(null);
  const isLastStep = activeStep === STEP_CONFIGS.length - 1;

  const handleCommitCollection = (rows: ModalRow[]) => {
    if (modalCollectionId) onReplaceCollection(modalCollectionId, rows);
  };

  return (
    <div className="annual-ambulatory-care">
      <ErrorCard chartMetaFormFields={formFieldMap} showErrorCard={formSubmittedCount > 0} />
      <h1>{ViewTitle.ANNUAL_AMBULATORY_CARE_RECORD}</h1>

      <div className="annual-ambulatory-care__flow">
        {STEP_CONFIGS.map((stepConfig, stepIndex) => {
          const isActive = stepIndex === activeStep;
          const canAccessStep = !isAuthor || stepIndex <= furthestStep;
          const isCompleted = Boolean(completedSteps[stepIndex]);
          const headerModifier = canAccessStep ? 'annual-ambulatory-care__step-header--clickable' : 'annual-ambulatory-care__step-header--locked';
          const indexClassName = [
            'annual-ambulatory-care__step-index',
            isActive && 'annual-ambulatory-care__step-index--active',
            isCompleted && !isActive && 'annual-ambulatory-care__step-index--completed'
          ]
            .filter(Boolean)
            .join(' ');

          return (
            <section className="annual-ambulatory-care__step" key={stepConfig.title}>
              <button className={`annual-ambulatory-care__step-header ${headerModifier}`} disabled={!canAccessStep} onClick={() => onSelectStep(stepIndex)} type="button">
                <span className={indexClassName}>{isCompleted && !isActive ? <ELSIcon name="checkmark" size="1x" color="white" title="Completed" /> : stepIndex + 1}</span>
                <span className={`annual-ambulatory-care__step-title${isActive ? ' annual-ambulatory-care__step-title--active' : ''}`}>{stepConfig.title}</span>
              </button>

              {isActive && (
                <div className="annual-ambulatory-care__panel">
                  <StepPanel
                    stepConfig={stepConfig}
                    formFieldMap={formFieldMap}
                    formSubmittedCount={formSubmittedCount}
                    isAuthor={isAuthor}
                    collections={collections}
                    onEditCollection={setModalCollectionId}
                    onReplaceCollection={onReplaceCollection}
                  />

                  <StepActions
                    activeStep={activeStep}
                    displayAuthoringData={displayAuthoringData}
                    enableDisplayRecordsButton={enableDisplayRecordsButton}
                    isAuthor={isAuthor}
                    isLastStep={isLastStep}
                    onBackStep={onBackStep}
                    onContinueStep={onContinueStep}
                  />
                </div>
              )}
            </section>
          );
        })}
      </div>

      {modalCollectionId && (
        <AnnualAmbulatoryCareAddModal
          config={COLLECTION_CONFIGS[modalCollectionId]}
          formFieldMap={formFieldMap}
          initialRows={collections[modalCollectionId]}
          onCommit={handleCommitCollection}
          onClose={() => setModalCollectionId(null)}
        />
      )}
    </div>
  );
};

AnnualAmbulatoryCareView.displayName = 'AnnualAmbulatoryCareView';
export default AnnualAmbulatoryCareView;
